import jsPDF from "jspdf";
import eOpsLogo from "../../../images/eops.png";
import {
  companyName,
  convertDatewithTime,
  getLogo,
  openJsPdfInNewTab,
} from "../../../services/Functions";
import { extractDiscoCodeFromSubDivisionCode } from "../../../utils/adminCompany";
import { buildCp52MeterPdfRows } from "./mrcReadingUtils";

/** Empty CP Form 52 of MRC defaults — real values come from getCP52 mapping */
const DUMMY = {
  batchNo: "",
  subDivision: "",
  accountNo: "",
  adjC: "A",
  adjNumber: "",
  adjDate: "",
  dueDate: "",
  billingMonth: "",
  extendedDueDate: "",
  tariffCode: "",
  tariffDisplay: "",
  enqNo: "",
  enqDate: "",
  consumerName: "",
  consumerAddress: "",
  chargeCols: [
    "Sales Tax",
    "G.S.T",
    "Electricity Duty",
    "NJ Surcharge",
    "Meter Rent",
    "G.S.T Subsidy",
    "Service Rent",
    "Variable Charges",
    "Fixed/Min. Charges",
    "LPF Penalty Charges",
    "Seasonal Charges",
    "With Holding / Income Tax",
    "EQ Surcharge",
    "LP Surcharge",
    "Etax",
    "Ftax",
  ],
  amountCharged: Array(16).fill("0"),
  amountToBeCharged: Array(16).fill("0"),
  amountToBeAdjusted: Array(16).fill("0"),
  totalAdjustment: "0",
  // noOfMonths: "",
  meterNo: "",
  setNo: "",
  presentAsCharged: "",
  presentToBeCharged: "",
  unitAdjusted: "",
  unitCharged: "",
  subsidyCharged: { fcSur: "0", aqa: "0", aqta2: "0" },
  subsidyToBeCharged: { fcSur: "0", aqa: "0", aqta2: "0" },
  subsidyToBeAdjusted: { fcSur: "0", aqa: "0", aqta2: "0" },
};

const COLORS = {
  line: [40, 48, 62],
  softLine: [120, 130, 145],
  headerBg: [232, 238, 247],
  sectionBg: [0, 57, 113],
  altRow: [246, 248, 252],
  white: [255, 255, 255],
  ink: [22, 28, 38],
  muted: [70, 80, 95],
  accent: [0, 57, 113],
  totalBg: [232, 245, 240],
  totalBorder: [30, 120, 90],
  totalNegBg: [254, 242, 242],
  totalNegBorder: [185, 28, 28],
  totalNegText: [220, 38, 38],
};

/** Readable font sizes for landscape A4 CP52 */
const FS = {
  micro: 6,
  small: 7,
  body: 8,
  medium: 8.5,
  large: 10,
  headerMeta: 7.5,
};

/** Row / cell heights (mm) */
const RH = {
  row: 6.5,
  head: 12,
  topTitle: 5.5,
  topLabel: 4.5,
  topValue: 6,
  meterHead: 7,
  subsidyHead: 6,
  subsidyRow: 5.5,
};

const setStroke = (doc, rgb = COLORS.line, width = 0.22) => {
  doc.setDrawColor(...rgb);
  doc.setLineWidth(width);
};

const setFill = (doc, rgb) => {
  doc.setFillColor(...rgb);
};

const setText = (doc, rgb = COLORS.ink) => {
  doc.setTextColor(...rgb);
};

/**
 * Draw a bordered cell with optional fill and wrapped text.
 */
const drawCell = (doc, x, y, w, h, text, opts = {}) => {
  const {
    fontSize = FS.body,
    align = "center",
    bold = false,
    vAlign = "middle",
    padding = 1,
    fill = null,
    stroke = COLORS.line,
    strokeWidth = 0.22,
    textColor = COLORS.ink,
    maxLines = 4,
    singleLine = false,
  } = opts;

  if (fill) {
    setFill(doc, fill);
    setStroke(doc, stroke, strokeWidth);
    doc.rect(x, y, w, h, "FD");
  } else {
    setStroke(doc, stroke, strokeWidth);
    doc.rect(x, y, w, h);
  }

  if (text === undefined || text === null || String(text).trim() === "") return;

  doc.setFont("helvetica", bold ? "bold" : "normal");
  setText(doc, textColor);

  const maxW = Math.max(w - padding * 2, 2);
  const textStr = String(text);

  if (singleLine) {
    let fs = fontSize;
    doc.setFontSize(fs);
    while (fs > 4.5 && doc.getTextWidth(textStr) > maxW) {
      fs -= 0.4;
      doc.setFontSize(fs);
    }
    const ty = y + h / 2 + fs * 0.14;
    if (align === "left") {
      doc.text(textStr, x + padding, ty, { align: "left" });
    } else if (align === "right") {
      doc.text(textStr, x + w - padding, ty, { align: "right" });
    } else {
      doc.text(textStr, x + w / 2, ty, { align: "center" });
    }
    return;
  }

  doc.setFontSize(fontSize);

  let lines = doc.splitTextToSize(textStr, maxW);
  if (lines.length > maxLines) {
    lines = lines.slice(0, maxLines);
    const last = lines[maxLines - 1];
    lines[maxLines - 1] =
      last.length > 2 ? `${last.slice(0, Math.max(last.length - 1, 1))}…` : last;
  }

  const lineH = fontSize * 0.42;
  const blockH = lines.length * lineH;
  let textY =
    vAlign === "top"
      ? y + padding + lineH * 0.85
      : y + h / 2 - blockH / 2 + lineH * 0.78;

  lines.forEach((line, i) => {
    const ty = textY + i * lineH;
    if (align === "left") {
      doc.text(line, x + padding, ty, { align: "left" });
    } else if (align === "right") {
      doc.text(line, x + w - padding, ty, { align: "right" });
    } else {
      doc.text(line, x + w / 2, ty, { align: "center" });
    }
  });
};

const splitWrappedLines = (doc, text, maxW, fontSize, fontStyle = "normal") => {
  doc.setFont("helvetica", fontStyle);
  doc.setFontSize(fontSize);
  const raw = String(text || "").trim();
  if (!raw) return [];
  return doc.splitTextToSize(raw, Math.max(maxW, 2));
};

/** Measure + layout name/address block for the consumer cell. */
const buildConsumerAddressBlock = (doc, { name, address, width, padding = 1.4 }) => {
  const innerW = Math.max(width - padding * 2, 10);
  const cleanName = String(name || "").trim();
  const cleanAddr = String(address || "").trim();

  let nameFont = FS.body;
  let addrFont = FS.small;
  let nameLines = splitWrappedLines(doc, cleanName, innerW, nameFont, "bold");
  let addrLines = splitWrappedLines(doc, cleanAddr, innerW, addrFont, "normal");

  const lineGap = cleanName && cleanAddr ? 0.6 : 0;
  const measureHeight = (nFont, aFont, nLines, aLines) => {
    const nLh = nFont * 0.42;
    const aLh = aFont * 0.42;
    return (
      (nLines.length ? nLines.length * nLh : 0) +
      lineGap +
      (aLines.length ? aLines.length * aLh : 0) +
      padding * 2
    );
  };

  let contentH = measureHeight(nameFont, addrFont, nameLines, addrLines);
  const minH = 10;
  const maxH = 22;

  if (contentH > maxH && addrLines.length > 2) {
    addrFont = FS.micro;
    addrLines = splitWrappedLines(doc, cleanAddr, innerW, addrFont, "normal");
    contentH = measureHeight(nameFont, addrFont, nameLines, addrLines);
  }

  if (contentH > maxH && nameLines.length > 1) {
    nameFont = FS.small;
    nameLines = splitWrappedLines(doc, cleanName, innerW, nameFont, "bold");
    contentH = measureHeight(nameFont, addrFont, nameLines, addrLines);
  }

  return {
    height: Math.min(maxH, Math.max(minH, contentH)),
    nameLines,
    addrLines,
    nameFont,
    addrFont,
    nameLineH: nameFont * 0.42,
    addrLineH: addrFont * 0.42,
    lineGap,
    padding,
    hasContent: Boolean(cleanName || cleanAddr),
  };
};

const drawConsumerAddressCell = (doc, x, y, w, h, block) => {
  setFill(doc, COLORS.white);
  setStroke(doc, COLORS.line, 0.22);
  doc.rect(x, y, w, h, "FD");

  if (!block.hasContent) {
    setText(doc, COLORS.ink);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(FS.body);
    doc.text("—", x + block.padding, y + block.padding + FS.body * 0.35);
    return;
  }

  let cy = y + block.padding + block.nameLineH * 0.8;

  if (block.nameLines.length) {
    setText(doc, COLORS.ink);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(block.nameFont);
    block.nameLines.forEach((line) => {
      doc.text(line, x + block.padding, cy, { align: "left" });
      cy += block.nameLineH;
    });
    cy += block.lineGap;
  }

  if (block.addrLines.length) {
    setText(doc, COLORS.muted);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(block.addrFont);
    if (!block.nameLines.length) {
      cy = y + block.padding + block.addrLineH * 0.8;
    }
    block.addrLines.forEach((line) => {
      doc.text(line, x + block.padding, cy, { align: "left" });
      cy += block.addrLineH;
    });
  }
};

const drawReportHeader = (doc, { companyCode, officerName, designation }) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const logo = getLogo(companyCode);
  const tallLogos = ["12", "13", "11", "16", "27", "48", "59"];
  const isTall = tallLogos.includes(String(companyCode));
  const logoWidth = isTall ? 17 : 21;
  const logoHeight = isTall ? 13 : 7.5;

  try {
    doc.addImage(logo, "PNG", 7, 3.5, logoWidth, logoHeight);
  } catch (e) {
    // ignore
  }

  try {
    doc.addImage(
      eOpsLogo,
      "PNG",
      isTall ? 26 : 9,
      isTall ? 9.5 : 12,
      15,
      5.5,
    );
  } catch (e) {
    // ignore
  }

  setText(doc, COLORS.accent);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11.5);
  doc.text(
    companyName(companyCode) || "Power Information Technology Company",
    pageWidth / 2,
    7.5,
    { align: "center" },
  );

  setText(doc, COLORS.ink);
  doc.setFontSize(10);
  doc.text("Meter Reading Correction", pageWidth / 2, 12.2, { align: "center" });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  setText(doc, COLORS.accent);
  doc.text("CP Form 52 of MRC — Adjustment Note", pageWidth / 2, 17, {
    align: "center",
  });

  const rightX = pageWidth - 7;
  setText(doc, COLORS.muted);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(FS.headerMeta);
  doc.text(convertDatewithTime(new Date()) || "", rightX, 6.5, { align: "right" });
  doc.text(`Name: ${officerName || "-"}`, rightX, 10.5, { align: "right" });
  doc.text(`Designation: ${designation || "-"}`, rightX, 14.5, { align: "right" });

  // subtle divider under header
  setStroke(doc, COLORS.softLine, 0.3);
  doc.line(7, 19.5, pageWidth - 7, 19.5);

  return 21.5;
};

/**
 * Generate CP Form 52 of MRC PDF — Adjustment Note form layout.
 */
const buildCPForm52OfMRCPdfDoc = (options = {}) => {
  const {
    companyCode = "10",
    officerName = "",
    designation = "",
    data = {},
    documents = [],
  } = options;
  const d = { ...DUMMY, ...data };

  const resolvedCompanyCode =
    extractDiscoCodeFromSubDivisionCode(d.subDivision) ||
    String(companyCode || "10").slice(0, 2);

  const meterRows = buildCp52MeterPdfRows(documents, d);

  const doc = new jsPDF("landscape", "mm", "a4", { compressPdf: true });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 5.5;
  const usable = pageWidth - margin * 2;

  // soft page wash
  setFill(doc, [252, 253, 255]);
  doc.rect(0, 0, pageWidth, pageHeight, "F");

  let y = drawReportHeader(doc, {
    companyCode: resolvedCompanyCode,
    officerName,
    designation,
  });

  // ── Top info strip ──────────────────────────────────────────────────────
  // Left band (Ref / Adj / Dates / Tariff) + right consumer column with
  // Name & Address below Enq/Date. Left filler matches consumer extra height.
  const titleH = RH.topTitle;
  const labelH = RH.topLabel;
  const valueH = RH.topValue;
  const leftBandH = titleH + labelH + valueH;
  const addrLabelH = 4.5;

  const refW = usable * 0.205;
  const adjW = usable * 0.185;
  const datesW = usable * 0.22;
  const tariffW = usable * 0.11;
  const consW = usable - (refW + adjW + datesW + tariffW);
  const leftTopW = refW + adjW + datesW + tariffW;

  const consumerBlock = buildConsumerAddressBlock(doc, {
    name: d.consumerName,
    address: d.consumerAddress,
    width: consW,
  });
  const addrValueH = consumerBlock.height;
  const rightExtraH = addrLabelH + addrValueH;
  const totalTopH = leftBandH + rightExtraH;

  let x = margin;
  const topY = y;

  // Reference Number
  drawCell(doc, x, topY, refW, titleH, "Reference Number", {
    bold: true,
    fontSize: FS.medium,
    fill: COLORS.headerBg,
    textColor: COLORS.accent,
  });
  const refSub = [
    { label: "Batch #", value: d.batchNo, w: refW * 0.22 },
    { label: "Sub Division", value: d.subDivision, w: refW * 0.3 },
    { label: "Account No", value: d.accountNo, w: refW * 0.48 },
  ];
  let sx = x;
  refSub.forEach((c) => {
    drawCell(doc, sx, topY + titleH, c.w, labelH, c.label, {
      fontSize: FS.small,
      bold: true,
      fill: COLORS.altRow,
      textColor: COLORS.muted,
    });
    drawCell(doc, sx, topY + titleH + labelH, c.w, valueH, c.value, {
      fontSize: FS.body,
      bold: true,
    });
    sx += c.w;
  });
  x += refW;

  // Adjustment Note
  drawCell(doc, x, topY, adjW, titleH, "Adjustment Note", {
    bold: true,
    fontSize: FS.medium,
    fill: COLORS.headerBg,
    textColor: COLORS.accent,
  });
  const adjSub = [
    { label: "C", value: d.adjC, w: adjW * 0.16 },
    { label: "Number", value: d.adjNumber, w: adjW * 0.44 },
    { label: "Date", value: d.adjDate, w: adjW * 0.4 },
  ];
  sx = x;
  adjSub.forEach((c) => {
    drawCell(doc, sx, topY + titleH, c.w, labelH, c.label, {
      fontSize: FS.small,
      bold: true,
      fill: COLORS.altRow,
      textColor: COLORS.muted,
    });
    drawCell(doc, sx, topY + titleH + labelH, c.w, valueH, c.value, {
      fontSize: FS.body,
      bold: true,
    });
    sx += c.w;
  });
  x += adjW;

  // Dates
  const dateCols = [
    { label: "Due Date", value: d.dueDate, w: datesW / 3 },
    { label: "Billing Month", value: d.billingMonth, w: datesW / 3 },
    { label: "Extended Due Date", value: d.extendedDueDate || "—", w: datesW / 3 },
  ];
  sx = x;
  dateCols.forEach((c) => {
    drawCell(doc, sx, topY, c.w, titleH, c.label, {
      fontSize: FS.small,
      bold: true,
      fill: COLORS.headerBg,
      textColor: COLORS.muted,
    });
    drawCell(doc, sx, topY + titleH, c.w, labelH + valueH, c.value, {
      fontSize: FS.body,
      bold: true,
    });
    sx += c.w;
  });
  x += datesW;

  // Tariff — full description (same as MRC table), not just code
  const tariffText = String(
    d.tariffDisplay || d.tariffDesc || d.tariffName || d.tariffCode || ""
  ).trim();
  drawCell(doc, x, topY, tariffW, titleH, "Tariff", {
    fontSize: FS.small,
    bold: true,
    fill: COLORS.headerBg,
    textColor: COLORS.muted,
  });
  drawCell(doc, x, topY + titleH, tariffW, labelH + valueH, tariffText, {
    fontSize: FS.body,
    bold: true,
    maxLines: 3,
  });
  x += tariffW;

  // Consumer column (right) — Enq / Date, then Name & Address with room to fit
  const enqW = consW * 0.42;
  const enqDateW = consW - enqW;
  const enqValueH = labelH + valueH;

  drawCell(doc, x, topY, enqW, titleH, "Enq. No", {
    fontSize: FS.small,
    bold: true,
    fill: COLORS.headerBg,
    textColor: COLORS.muted,
  });
  drawCell(doc, x + enqW, topY, enqDateW, titleH, "Date", {
    fontSize: FS.small,
    bold: true,
    fill: COLORS.headerBg,
    textColor: COLORS.muted,
  });
  drawCell(doc, x, topY + titleH, enqW, enqValueH, d.enqNo, {
    fontSize: FS.body,
    bold: true,
  });
  drawCell(doc, x + enqW, topY + titleH, enqDateW, enqValueH, d.enqDate, {
    fontSize: FS.body,
    bold: true,
  });

  const addrTop = topY + leftBandH;
  drawCell(doc, x, addrTop, consW, addrLabelH, "Name & Address of Consumer", {
    fontSize: FS.small,
    bold: true,
    align: "left",
    fill: COLORS.altRow,
    textColor: COLORS.muted,
    padding: 1.2,
  });
  drawConsumerAddressCell(
    doc,
    x,
    addrTop + addrLabelH,
    consW,
    addrValueH,
    consumerBlock,
  );

  // Left filler under Ref/Adj/Dates/Tariff so bottom aligns with address
  drawCell(doc, margin, addrTop, leftTopW, rightExtraH, "", {
    fill: COLORS.white,
  });

  y = topY + totalTopH + 1;

  // ── Main charges table ──────────────────────────────────────────────────
  const labelW = 28;
  const colCount = d.chargeCols.length;
  const colW = (usable - labelW) / colCount;
  const headH = RH.head;
  const rowH = RH.row;

  drawCell(doc, margin, y, labelW, headH, "", {
    fill: COLORS.headerBg,
  });
  d.chargeCols.forEach((col, i) => {
    drawCell(doc, margin + labelW + i * colW, y, colW, headH, col, {
      fontSize: FS.micro,
      bold: true,
      fill: COLORS.headerBg,
      textColor: COLORS.ink,
      padding: 0.5,
    });
  });
  y += headH;

  const chargeRows = [
    { label: "Amount Charged", values: d.amountCharged },
    { label: "Amount to be Charged", values: d.amountToBeCharged },
    { label: "Amount to be Adjusted", values: d.amountToBeAdjusted },
  ];
  chargeRows.forEach((row, rowIdx) => {
    const fill = rowIdx % 2 === 1 ? COLORS.altRow : COLORS.white;
    drawCell(doc, margin, y, labelW, rowH, row.label, {
      fontSize: FS.small,
      bold: true,
      align: "left",
      fill,
      padding: 1,
    });
    row.values.forEach((val, i) => {
      const isHighlight = String(val) !== "0" && String(val) !== "";
      drawCell(doc, margin + labelW + i * colW, y, colW, rowH, val, {
        fontSize: isHighlight ? FS.body : FS.small,
        bold: isHighlight,
        fill,
        textColor: isHighlight ? COLORS.accent : COLORS.ink,
      });
    });
    y += rowH;
  });

  // Note text on left side
  y += 1;
  setText(doc, COLORS.muted);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(FS.small);
  doc.text("* G.S.T Subsidy is not included in Total Adjustments.", margin, y + 3.5);

  // No. of Months + Total Adjustment boxes on right side (same row as note)
  const cellH = 7;
  // const monthsLabelW = 24;
  // const monthsValueW = 14;
  const totalLabelW = 30;
  const totalValueW = 26;
  const totalRowW = totalLabelW + totalValueW;
  const startX = margin + usable - totalRowW;

  // drawCell(doc, startX, y, monthsLabelW, cellH, "No. of Months", {
  //   fontSize: FS.small,
  //   bold: true,
  //   fill: COLORS.altRow,
  // });
  // drawCell(doc, startX + monthsLabelW, y, monthsValueW, cellH, String(d.noOfMonths || "—"), {
  //   fontSize: FS.large,
  //   bold: true,
  //   fill: COLORS.white,
  // });
  const totalAdjNum = Number(String(d.totalAdjustment ?? "").replace(/,/g, "").trim());
  const isNegativeTotal = Number.isFinite(totalAdjNum) && totalAdjNum < 0;
  const totalFill = isNegativeTotal ? COLORS.totalNegBg : COLORS.totalBg;
  const totalColor = isNegativeTotal ? COLORS.totalNegText : COLORS.totalBorder;
  const totalStroke = isNegativeTotal ? COLORS.totalNegBorder : COLORS.totalBorder;

  drawCell(doc, startX, y, totalLabelW, cellH, "Total Adjustment", {
    fontSize: FS.small,
    bold: true,
    fill: totalFill,
    textColor: totalColor,
  });
  drawCell(doc, startX + totalLabelW, y, totalValueW, cellH, String(d.totalAdjustment || "0"), {
    fontSize: FS.large,
    bold: true,
    fill: totalFill,
    textColor: totalColor,
    stroke: totalStroke,
    strokeWidth: 0.5,
  });

  y += cellH + 1;

  // ── Meter + WAPDA employees ─────────────────────────────────────────────
  const leftW = usable * 0.5;
  const gap = 2;
  const rightW = usable - leftW - gap;
  const midY = y;
  const empTitleH = 4.5;
  const numMeterRows = Math.min(meterRows.length, 12);
  const meterHeadH = RH.meterHead;
  const meterRowH =
    numMeterRows > 10 ? 4.5 : numMeterRows > 6 ? 5 : 5.5;
  const meterValueFont =
    numMeterRows > 10 ? FS.small : FS.body;

  // Column definitions for meter table
  const meterColDefs = [
    { label: "Meter ID\nFor Gen.", key: "meterIdForGen", w: leftW * 0.1 },
    { label: "Meter ID\nFor MDI", key: "meterIdForMdi", w: leftW * 0.1 },
    { label: "Set No.", key: "setNo", w: leftW * 0.08 },
    { label: "Meter No.", key: "meterNo", w: leftW * 0.14 },
    { label: "Present Reading\nAs Charged", key: "presentAsCharged", w: leftW * 0.16 },
    { label: "Present Reading\nTo be Charged", key: "presentToBeCharged", w: leftW * 0.16 },
    { label: "Unit\nAdjusted", key: "unitAdjusted", w: leftW * 0.13 },
    { label: "Unit\nCharged", key: "unitCharged", w: leftW * 0.13 },
  ];
  const meterSum = meterColDefs.reduce((s, c) => s + c.w, 0);
  meterColDefs.forEach((c) => {
    c.w = (c.w / meterSum) * leftW;
  });

  // MRC Title
  drawCell(doc, margin, midY, leftW, empTitleH, "--------MRC--------", {
    fontSize: FS.small,
    bold: true,
    fill: COLORS.headerBg,
    textColor: COLORS.accent,
    stroke: COLORS.line,
  });

  // Meter table headers
  let mx = margin;
  meterColDefs.forEach((c) => {
    drawCell(doc, mx, midY + empTitleH, c.w, meterHeadH, c.label, {
      fontSize: FS.small,
      bold: true,
      fill: COLORS.headerBg,
      textColor: COLORS.muted,
      padding: 0.4,
    });
    mx += c.w;
  });

  // Meter table data rows (max 12)
  let meterDataY = midY + empTitleH + meterHeadH;
  const rowsToRender = meterRows.slice(0, 12);
  rowsToRender.forEach((row, rowIdx) => {
    const fill = rowIdx % 2 === 1 ? COLORS.altRow : COLORS.white;
    mx = margin;
    meterColDefs.forEach((c) => {
      const val = row[c.key] || "";
      const isHighlight = c.key === "unitAdjusted" && val !== "" && val !== "0";
      drawCell(doc, mx, meterDataY, c.w, meterRowH, val || "—", {
        fontSize: isHighlight ? FS.body : meterValueFont,
        bold: isHighlight,
        fill,
        textColor: isHighlight ? COLORS.accent : COLORS.ink,
      });
      mx += c.w;
    });
    meterDataY += meterRowH;
  });

  // WAPDA Employees section (same height as meter section)
  const meterSectionH = empTitleH + meterHeadH + (numMeterRows * meterRowH);
  const empX = margin + leftW + gap;

  drawCell(doc, empX, midY, rightW, empTitleH, "For WAPDA Employees", {
    fontSize: FS.small,
    bold: true,
    fill: COLORS.headerBg,
    textColor: COLORS.accent,
    stroke: COLORS.line,
  });

  const empCols = [
    "D.D. Officer Code",
    "TR Sur",
    "Income Tax",
    "Var Chrg",
    "EQ Sur",
    "FC Sur",
    "G.S.T",
    "NJ Sur",
    "AQA",
    "AQTA2",
    "AQTA3",
    "ADMC",
    "Total Amnt",
    "Units Adj.",
  ];
  const empColW = rightW / empCols.length;
  const empHeadH = meterHeadH;
  const empDataH = meterSectionH - empTitleH - empHeadH;

  let ex = empX;
  empCols.forEach((c) => {
    drawCell(doc, ex, midY + empTitleH, empColW, empHeadH, c, {
      fontSize: FS.micro,
      bold: true,
      fill: COLORS.headerBg,
      textColor: COLORS.muted,
      padding: 0.3,
    });
    drawCell(doc, ex, midY + empTitleH + empHeadH, empColW, empDataH, "", {
      fontSize: FS.small,
      fill: COLORS.white,
    });
    ex += empColW;
  });

  y = midY + meterSectionH + 1.5;

  // ── Subsidies Adjustments ───────────────────────────────────────────────
  drawCell(doc, margin, y, usable, 4.5, "Subsidies Adjustments", {
    fontSize: FS.small,
    bold: true,
    fill: COLORS.headerBg,
    textColor: COLORS.accent,
    stroke: COLORS.line,
  });
  y += 4.5;

  const groupDefs = [
    { title: "Federal", subs: ["Elect. Duty", "OTHERS", "NJ Sur", "FC-Sur"] },
    { title: "Provincial", subs: ["Elect. Duty", "OTHERS", "NJ Sur", "FC-Sur"] },
    { title: "Local Bodies", subs: ["ED.", "OTHERS"] },
    { title: "WAPDA", subs: ["Elect. Duty", "OTHERS", "NJ Sur", "FC-Sur"] },
  ];
  const trailing = [
    "ITS-Tax",
    "FC-Sur",
    "TR-Sur",
    "R-Stax",
    "Addl. Stax",
    "D-Itax",
    "ISP-2019",
    "ZRIR",
    "AQA",
    "AQTA2",
    "ADMC",
    "AQT3",
  ];

  const allSubHeaders = [];
  groupDefs.forEach((g) =>
    g.subs.forEach((s) => allSubHeaders.push({ group: g.title, label: s })),
  );
  trailing.forEach((t) => allSubHeaders.push({ group: "", label: t }));

  const subLabelW = 24;
  const subColW = (usable - subLabelW) / allSubHeaders.length;
  const gHeadH = 4;
  const sHeadH = RH.subsidyHead;
  const sRowH = RH.subsidyRow;

  let gx = margin + subLabelW;
  drawCell(doc, margin, y, subLabelW, gHeadH + sHeadH, "", {
    fill: COLORS.headerBg,
  });
  groupDefs.forEach((g) => {
    const gw = g.subs.length * subColW;
    drawCell(doc, gx, y, gw, gHeadH, g.title, {
      fontSize: FS.small,
      bold: true,
      fill: COLORS.headerBg,
      textColor: COLORS.accent,
    });
    gx += gw;
  });
  drawCell(doc, gx, y, trailing.length * subColW, gHeadH, "", {
    fill: COLORS.headerBg,
  });
  y += gHeadH;

  let hx = margin + subLabelW;
  allSubHeaders.forEach((h) => {
    drawCell(doc, hx, y, subColW, sHeadH, h.label, {
      fontSize: FS.micro,
      bold: true,
      fill: COLORS.altRow,
      textColor: COLORS.muted,
      padding: 0.3,
    });
    hx += subColW;
  });
  y += sHeadH;

  const findColIndex = (label) =>
    allSubHeaders.findIndex((h, idx) => {
      if (label === "FC-Sur") {
        return (
          h.label === "FC-Sur" &&
          idx >= allSubHeaders.length - trailing.length
        );
      }
      return h.label === label;
    });

  const buildSubsidyRow = (src) => {
    const vals = allSubHeaders.map(() => "");
    const fcIdx = findColIndex("FC-Sur");
    const aqaIdx = findColIndex("AQA");
    const aqta2Idx = findColIndex("AQTA2");
    if (fcIdx >= 0) vals[fcIdx] = src.fcSur;
    if (aqaIdx >= 0) vals[aqaIdx] = src.aqa;
    if (aqta2Idx >= 0) vals[aqta2Idx] = src.aqta2;
    return vals;
  };

  const subsidyRows = [
    { label: "Amount Charged", values: buildSubsidyRow(d.subsidyCharged) },
    { label: "Amt to be Charged", values: buildSubsidyRow(d.subsidyToBeCharged) },
    { label: "Amt to be Adjusted", values: buildSubsidyRow(d.subsidyToBeAdjusted) },
  ];

  subsidyRows.forEach((row, rowIdx) => {
    const fill = rowIdx % 2 === 1 ? COLORS.altRow : COLORS.white;
    drawCell(doc, margin, y, subLabelW, sRowH, row.label, {
      fontSize: FS.small,
      bold: true,
      align: "left",
      fill,
      padding: 0.8,
    });
    row.values.forEach((val, i) => {
      const isHighlight = String(val) !== "0" && String(val) !== "";
      drawCell(doc, margin + subLabelW + i * subColW, y, subColW, sRowH, val, {
        fontSize: isHighlight ? FS.small : FS.micro,
        bold: isHighlight,
        fill,
        textColor: isHighlight ? COLORS.accent : COLORS.ink,
        padding: 0.25,
        singleLine: true,
      });
    });
    y += sRowH;
  });

  // ── Signatures ──────────────────────────────────────────────────────────
  y = Math.max(y + 4, pageHeight - 12);
  setText(doc, COLORS.ink);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(FS.small);
  doc.text("Signature of CSO/SDO/RO", margin + 18, y);
  setStroke(doc, COLORS.line, 0.3);
  doc.line(margin + 8, y + 4, margin + 78, y + 4);

  doc.text("Date (Stamp)", pageWidth - margin - 48, y);
  doc.line(pageWidth - margin - 62, y + 4, pageWidth - margin - 8, y + 4);

  return doc;
};

/** Merged form data used by PDF + prepare popup table */
export const buildCPForm52OfMRCData = (data = {}) => ({ ...DUMMY, ...data });

/** Build jsPDF doc without opening */
export const createCPForm52OfMRCPdfDoc = (options = {}) =>
  buildCPForm52OfMRCPdfDoc(options);

/** PDF blob URL for iframe preview (caller should revoke when done) */
export const getCPForm52OfMRCPdfBlobUrl = (options = {}) => {
  const doc = buildCPForm52OfMRCPdfDoc(options);
  const pdfBlob = doc.output("blob");
  return URL.createObjectURL(new Blob([pdfBlob], { type: "application/pdf" }));
};

/**
 * Generate CP Form 52 of MRC PDF and open in a new tab (no download).
 */
export const downloadCPForm52OfMRCPdf = (options = {}) => {
  const doc = buildCPForm52OfMRCPdfDoc(options);
  openJsPdfInNewTab(doc, "CP-Form-52-of-MRC.pdf");
  return doc;
};

export default downloadCPForm52OfMRCPdf;
